// Lokal tid på en plats. Ägaren skriver ankomsten i platsens egen tid, till exempel fredag
// 18.00 i Trysil, och molnet räknar om den till UTC med platsens tidszon (b3 avsnitt 3 E).
//
// Sommartiden: en lokal tid som inte finns (klockan ställs fram) flyttas fram lika mycket som
// klockan, och en tid som finns två gånger (klockan ställs tillbaka) blir den första. Det är
// samma regel som "compatible" i JavaScripts Temporal.

/** Tidszonens förskjutning från UTC vid en tidpunkt, i millisekunder. */
export function tidszonensForskjutning(utc: number, tidszon: string): number {
  const delar = new Intl.DateTimeFormat("en-US", {
    timeZone: tidszon,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(utc);
  const v = (typ: string) => Number(delar.find((d) => d.type === typ)?.value ?? 0);
  const lokal = Date.UTC(v("year"), v("month") - 1, v("day"), v("hour"), v("minute"), v("second"));
  return lokal - Math.floor(utc / 1000) * 1000;
}

let kandaTidszoner: Map<string, string> | undefined;

/**
 * Tidszonen i IANA:s stavning, oavsett stora och små bokstäver (b6), eller null om den inte
 * finns. "Europe/madrid" och "europe/madrid" blir "Europe/Madrid". Ett gammalt namn som
 * "US/Pacific" blir det namn som gäller nu.
 */
export function normaliseraTidszon(tidszon: string): string | null {
  const t = tidszon.trim();
  if (!/^(?:[A-Za-z_]+(?:\/[A-Za-z0-9_+-]+)+|UTC)$/i.test(t)) return null;
  if (t.toUpperCase() === "UTC") return "UTC";
  if (!kandaTidszoner) {
    kandaTidszoner = new Map();
    for (const z of Intl.supportedValuesOf("timeZone")) kandaTidszoner.set(z.toLowerCase(), z);
  }
  const kand = kandaTidszoner.get(t.toLowerCase());
  if (kand) return kand;
  try {
    return new Intl.DateTimeFormat("sv-SE", { timeZone: t }).resolvedOptions().timeZone;
  } catch {
    return null;
  }
}

/** Sant om tidszonen finns, till exempel "Europe/Oslo", med vilka bokstäver som helst. */
export function arTidszon(tidszon: string): boolean {
  return normaliseraTidszon(tidszon) !== null;
}

const LOKAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/**
 * En lokal tid ("2026-10-23T18:00") i en tidszon, i millisekunder sedan 1970 (UTC).
 * Ger null om texten inte är en tid.
 */
export function lokalTid(text: string, tidszon: string): number | null {
  const m = LOKAL.exec(text);
  if (!m) return null;
  const [ar, man, dag, tim, min] = m.slice(1).map(Number) as [
    number,
    number,
    number,
    number,
    number,
  ];
  if (man < 1 || man > 12 || dag < 1 || dag > 31 || tim > 23 || min > 59) return null;
  const naiv = Date.UTC(ar, man - 1, dag, tim, min);
  if (new Date(naiv).getUTCDate() !== dag) return null;
  // Förskjutningen före och efter den lokala tiden. De är olika bara nära en omställning.
  const fore = tidszonensForskjutning(naiv - 36 * 3600_000, tidszon);
  const efter = tidszonensForskjutning(naiv + 36 * 3600_000, tidszon);
  const kandidater = [...new Set([naiv - fore, naiv - efter])]
    .filter((t) => t + tidszonensForskjutning(t, tidszon) === naiv)
    .sort((a, b) => a - b);
  // Finns tiden två gånger blir det den första. Finns den inte flyttas den fram.
  return kandidater[0] ?? naiv - fore;
}

/** En tidpunkt som lokal tid i en tidszon, i samma form som lokalTid läser. */
export function somLokalTid(ms: number, tidszon: string): string {
  const t = new Date(ms + tidszonensForskjutning(ms, tidszon));
  const tva = (n: number) => String(n).padStart(2, "0");
  return `${t.getUTCFullYear()}-${tva(t.getUTCMonth() + 1)}-${tva(t.getUTCDate())}T${tva(t.getUTCHours())}:${tva(t.getUTCMinutes())}`;
}
